import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { AppScenario } from '../data/mock-data';

interface MapSurfaceProps {
  scenarioState: AppScenario;
  onSelectPhoto: (id: string) => void;
  selectedPhotoId: string | null;
  onRetry?: () => void;
  isFilteredEmpty?: boolean;
}

const configuredMapStyleUrl = import.meta.env.VITE_MAP_STYLE_URL?.trim();

const defaultMapStyle: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    'osm-raster': {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    {
      id: 'osm-raster',
      type: 'raster',
      source: 'osm-raster',
    },
  ],
};

export const MapSurface: React.FC<MapSurfaceProps> = ({
  scenarioState,
  onSelectPhoto,
  selectedPhotoId,
  onRetry,
  isFilteredEmpty = false,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<{ [key: string]: maplibregl.Marker }>({});
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (scenarioState.map.status === 'loading' || scenarioState.map.status === 'error' || !mapContainer.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: configuredMapStyleUrl || defaultMapStyle,
      center: [126.36, 36.76],
      zoom: 10,
      attributionControl: { compact: true },
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', () => {
      map.resize();
      setMapLoaded(true);
    });

    map.on('error', (event) => {
      console.warn('지도 타일을 불러오지 못했습니다.', event.error?.message);
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      setMapLoaded(false);
    };
  }, [scenarioState.map.status]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    const map = mapRef.current;
    const mappedPhotos = scenarioState.photos.filter(
      (p) => p.status === 'ready' && p.hasGps && p.longitude != null && p.latitude != null
    );

    // Clear old markers
    Object.values(markersRef.current).forEach((marker) => marker.remove());
    markersRef.current = {};

    if (mappedPhotos.length > 0) {
      const bounds = new maplibregl.LngLatBounds();

      mappedPhotos.forEach((photo) => {
        if (photo.longitude == null || photo.latitude == null) return;

        const el = document.createElement('button');
        el.className = 'marker';
        el.setAttribute('type', 'button');
        el.setAttribute('data-selected', String(photo.id === selectedPhotoId));
        el.setAttribute('aria-label', '공사 사진 열기');
        el.onclick = () => onSelectPhoto(photo.id);

        if (photo.previewUrl) {
          const img = document.createElement('img');
          img.className = 'marker__image';
          img.src = photo.previewUrl;
          el.appendChild(img);
        } else {
          const fallback = document.createElement('span');
          fallback.className = 'marker__fallback';
          fallback.innerText = '사진';
          el.appendChild(fallback);
        }

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([photo.longitude, photo.latitude])
          .addTo(map);

        markersRef.current[photo.id] = marker;
        bounds.extend([photo.longitude, photo.latitude]);
      });

      map.fitBounds(bounds, { padding: 56, maxZoom: 14 });
    }
  }, [scenarioState.photos, selectedPhotoId, mapLoaded, onSelectPhoto]);

  if (scenarioState.map.status === 'loading') {
    return (
      <div className="map-state" role="status">
        <strong>굴착 위치 지도를 준비하고 있습니다</strong>
        <p>사진 GPS와 썸네일을 불러오는 중입니다.</p>
      </div>
    );
  }

  if (scenarioState.map.status === 'error') {
    return (
      <div className="map-state" role="alert">
        <strong>지도를 불러오지 못했습니다</strong>
        <p>사진 목록과 지도 타일 연결 상태를 확인한 뒤 다시 불러오세요.</p>
        {onRetry && (
          <button className="button button--secondary" type="button" onClick={onRetry}>
            다시 불러오기
          </button>
        )}
      </div>
    );
  }

  const mappedPhotosCount = scenarioState.photos.filter((p) => p.status === 'ready' && p.hasGps).length;
  const showEmptyNotice = scenarioState.map.status === 'empty' || mappedPhotosCount === 0;

  return (
    <main className="panel map-panel" aria-label="굴착공사 사진 위치 지도">
      <div className="map-surface" aria-label="굴착공사 사진 위치 지도" ref={mapContainer}>
        {showEmptyNotice && (
          <div className="map-empty-notice" role="status">
            <strong>{isFilteredEmpty ? '선택한 기간에 위치 사진이 없습니다' : '지도에 표시할 사진이 없습니다'}</strong>
            <span>
              {isFilteredEmpty
                ? '기간을 초기화하거나 다른 기간을 선택하면 위치가 있는 사진을 다시 확인할 수 있습니다.'
                : '위치를 읽을 수 있는 굴착공사 사진을 등록하면 지도에 썸네일이 표시됩니다.'}
            </span>
          </div>
        )}
      </div>
    </main>
  );
};
